import { useCallback, useEffect, useRef, useState } from "react";
import { writeBrowserRuntimeReloadBreadcrumb } from "@/lib/browser-runtime-reload-breadcrumb";
import { debugWarn } from "@/stores/debug-logs";

const SW_UPDATE_POLL_INTERVAL_MS = 60_000;
// 60 seconds keeps update checks lightweight while still surfacing deploys quickly.
// Users may wait up to this interval to see the refresh prompt after a deploy.
const DEPLOYMENT_UPDATE_POLL_INTERVAL_MS = 60_000;
// Match the SW cadence so we do not create a noisier poll cycle than needed.
// If deploys happen very frequently, this still intentionally trades immediacy for lower request volume.
const APP_ENTRY_SCRIPT_SELECTOR = 'script[type="module"][src]';
const APP_SHELL_URL = "/index.html";
const DEFAULT_PREVIEW_DOMAIN = "preview-cord.xyz";

function getPreviewDomain(): string {
  return import.meta.env.VITE_PREVIEW_DOMAIN || DEFAULT_PREVIEW_DOMAIN;
}

function isCordPreviewHost(hostname: string): boolean {
  const previewDomain = getPreviewDomain();
  return hostname === previewDomain || hostname.endsWith(`.${previewDomain}`);
}

function readEntrypointScriptUrlFromDocument(input: {
  doc: Document;
  baseUrl: string;
}): string | null {
  const script = input.doc.querySelector<HTMLScriptElement>(APP_ENTRY_SCRIPT_SELECTOR);
  const src = script?.getAttribute("src");
  if (!src) return null;

  try {
    return new URL(src, input.baseUrl).href;
  } catch {
    return null;
  }
}

function parseEntrypointScriptUrlFromHtml(input: {
  html: string;
  baseUrl: string;
}): string | null {
  const nextDocument = new DOMParser().parseFromString(input.html, "text/html");
  return readEntrypointScriptUrlFromDocument({
    doc: nextDocument,
    baseUrl: input.baseUrl,
  });
}

/**
 * Manages service worker registration with automatic update detection.
 *
 * - Registers the SW and polls for updates every 60s
 * - Polls index.html for a new app entry chunk URL after deployments
 * - Exposes a UI signal so users can refresh on their own timing
 * - Guards against first-install false positives (no prior controller)
 *
 * `sw.js` handles push and may activate quickly with skipWaiting/claim,
 * but we avoid auto-reloading. Instead, we surface a non-blocking refresh
 * prompt when we detect either a SW update or a new deployment shell.
 */
export function useSwUpdate() {
  const [updateFound, setUpdateFound] = useState(false);
  const updateFoundRef = useRef(false);
  const currentEntrypointScriptUrlRef = useRef<string | null>(null);

  const markUpdateFound = useCallback(() => {
    if (updateFoundRef.current) return;
    updateFoundRef.current = true;
    setUpdateFound(true);
  }, []);

  const refresh = useCallback(() => {
    writeBrowserRuntimeReloadBreadcrumb({
      cause: "deployment_refresh_requested",
      path: window.location.pathname,
    });
    debugWarn("session.lifecycle", "deployment_refresh_requested", {
      path: window.location.pathname,
    });
    console.warn("deployment_refresh_requested", {
      path: window.location.pathname,
    });
    window.location.reload();
  }, []);

  useEffect(() => {
    currentEntrypointScriptUrlRef.current = readEntrypointScriptUrlFromDocument({
      doc: document,
      baseUrl: window.location.origin,
    });

    let disposed = false;

    const checkForNewDeployment = async () => {
      if (disposed || updateFoundRef.current) return;

      try {
        const response = await fetch(APP_SHELL_URL, { cache: "no-store" });
        if (!response.ok) return;
        const html = await response.text();

        const latestEntrypointScriptUrl = parseEntrypointScriptUrlFromHtml({
          html,
          baseUrl: window.location.origin,
        });

        const currentEntrypointScriptUrl = currentEntrypointScriptUrlRef.current;
        if (!latestEntrypointScriptUrl || !currentEntrypointScriptUrl) return;

        if (latestEntrypointScriptUrl !== currentEntrypointScriptUrl) {
          markUpdateFound();
        }
      } catch {
        // Ignore transient network issues; next poll can recover.
      }
    };

    void checkForNewDeployment();
    const intervalId = window.setInterval(() => {
      void checkForNewDeployment();
    }, DEPLOYMENT_UPDATE_POLL_INTERVAL_MS);

    return () => {
      disposed = true;
      window.clearInterval(intervalId);
    };
  }, [markUpdateFound]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    // In Cord preview origins we run a separate proxy service worker
    // (`/__cord-sw.js`) for localhost URL rewriting. Registering the app's
    // own `/sw.js` here causes worker takeover churn and reload loops.
    if (isCordPreviewHost(window.location.hostname)) return;

    const activeControllerScriptUrl =
      navigator.serviceWorker.controller?.scriptURL ?? "";
    if (activeControllerScriptUrl.includes("/__cord-sw.js")) return;

    // Track whether a controller existed when this page loaded.
    // If there was no controller (first visit / first SW install), skip
    // prompting because this load is already current.
    const hadControllerOnLoad = !!navigator.serviceWorker.controller;
    let disposed = false;
    let swUpdateIntervalId: number | null = null;
    let registrationForCleanup: ServiceWorkerRegistration | null = null;
    let registrationUpdateFoundListener: (() => void) | null = null;

    const clearSwUpdatePollingInterval = () => {
      if (swUpdateIntervalId === null) return;
      window.clearInterval(swUpdateIntervalId);
      swUpdateIntervalId = null;
    };

    // When the active controller changes (new SW took over), prompt refresh.
    const onControllerChange = () => {
      if (!hadControllerOnLoad) return; // first-install, skip
      markUpdateFound();
    };

    navigator.serviceWorker.addEventListener(
      "controllerchange",
      onControllerChange,
    );

    // Register and set up periodic update checks
    navigator.serviceWorker
      .register("/sw.js")
      .then((registration) => {
        if (disposed) return;
        registrationForCleanup = registration;

        // If there's already a waiting worker (e.g., user had an old tab open),
        // surface refresh prompt immediately.
        if (registration.waiting) {
          markUpdateFound();
        }

        // Listen for future updates
        const onRegistrationUpdateFound = () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener("statechange", () => {
            // When the new worker is installed and waiting, mark update found.
            // Since sw.js calls skipWaiting(), it transitions quickly.
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              markUpdateFound();
            }
          });
        };

        registration.addEventListener("updatefound", onRegistrationUpdateFound);
        registrationUpdateFoundListener = onRegistrationUpdateFound;

        // Periodically check for a new SW version
        swUpdateIntervalId = window.setInterval(() => {
          registration.update().catch(() => {
            // update() can fail if offline or server error — ignore
          });
        }, SW_UPDATE_POLL_INTERVAL_MS);
      })
      .catch(() => {
        // SW registration failed — push notifications won't work,
        // but app still functions
      });

    return () => {
      disposed = true;
      clearSwUpdatePollingInterval();
      if (registrationForCleanup && registrationUpdateFoundListener) {
        registrationForCleanup.removeEventListener(
          "updatefound",
          registrationUpdateFoundListener,
        );
      }
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        onControllerChange,
      );
    };
  }, [markUpdateFound]);

  return { updateFound, refresh };
}
